import type { ButtonHTMLAttributes, MouseEvent, ReactNode } from 'react';

import { cn } from '@shared/lib/cn';
import { Icon } from '@shared/ui/icon';
import { Spinner } from '@shared/ui/spinner';

import {
  CIRCLE_VARIANT_CLASS,
  DOT_CLASS,
  SHAPE_CLASS,
  SIZE_CLASS,
  SPINNER_SIZE,
  VARIANT_CLASS,
} from './icon-button.constant';

export type IconButtonVariant = 'primary' | 'outline' | 'ghost';

export type IconButtonSize = 'xs' | 'sm' | 'md' | 'lg';

export type IconButtonStatus = 'default' | 'loading';

export type IconButtonShape = 'square' | 'circle';

export interface IconButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  /** 아이콘만 있으므로 필수. 보조기기가 읽는 버튼 이름 */
  'aria-label': string;
  /** Figma `Icon`. 없으면 24 크기의 빈 아이콘 자리 */
  icon?: ReactNode;
  /** Figma `Dot`. 오른쪽 위 빨간 점(알림) */
  showDot?: boolean;
  /** Figma `variant`. 기본 primary */
  variant?: IconButtonVariant;
  /** Figma `size`(xs 24 · sm 32 · md 40 · lg 48). 기본 sm */
  size?: IconButtonSize;
  /** Figma `status`. loading 이면 아이콘을 흐리게 하고 Spinner 를 겹친다 */
  status?: IconButtonStatus;
  /**
   * Figma `shape`. square = 모서리 4(기본), circle = 원형 면.
   * 원형 면 위에 아이콘을 얹을 때(variant=primary 면 primary 원 + 흰 아이콘 `fg-inverse`) 쓴다 —
   * 원이 그려진 glyph(`search-circle` 등)에 색을 넣지 않고 원 없는 glyph(`search`) + 원형 면으로 그린다
   */
  shape?: IconButtonShape;
  className?: string;
}

/** 아이콘 하나로 동작을 실행하는 정사각형(또는 원형) 버튼. 아이콘은 넘겨받은 크기 그대로(없으면 24 자리) */
export function IconButton({
  icon,
  showDot = false,
  variant = 'primary',
  size = 'sm',
  status = 'default',
  shape = 'square',
  className,
  type = 'button',
  onClick,
  ...rest
}: IconButtonProps) {
  const loading = status === 'loading';

  function handleClick(event: MouseEvent<HTMLButtonElement>) {
    if (loading) {
      event.preventDefault();
      return;
    }
    onClick?.(event);
  }

  return (
    <button
      type={type}
      data-size={size}
      data-status={status}
      data-shape={shape}
      aria-busy={loading || undefined}
      aria-disabled={loading || undefined}
      onClick={handleClick}
      className={cn(
        'relative inline-flex shrink-0 cursor-pointer items-center justify-center disabled:cursor-not-allowed',
        SHAPE_CLASS[shape],
        VARIANT_CLASS[variant],
        shape === 'circle' && CIRCLE_VARIANT_CLASS[variant],
        SIZE_CLASS[size],
        loading && 'pointer-events-none',
        className,
      )}
      {...rest}
    >
      <span
        data-slot="icon"
        className={cn('inline-flex shrink-0', loading && 'opacity-(--opacity-disabled)')}
      >
        {icon ?? <Icon size="lg" />}
      </span>
      {loading ? (
        <Spinner
          data-slot="spinner"
          aria-hidden
          size={SPINNER_SIZE[size]}
          tone="primary"
          className="absolute inset-0 m-auto"
        />
      ) : null}
      {showDot ? (
        <span
          data-slot="dot"
          aria-hidden
          className={cn(
            'absolute rounded-full bg-bg-danger ring-2 ring-bg-surface',
            DOT_CLASS[size],
          )}
        />
      ) : null}
    </button>
  );
}
